import { graphql } from '@/graphql-setup'
import { UserFragments } from '@/modules/users/fragments'
import { gql } from '@apollo/client'

export const USER_TIME_ENTRIES_QUERY = graphql(`
  query UserTimeEntries(
    $user: ID
    $datetimeStartLt: DateTime
    $datetimeStartGt: DateTime
    $datetimeEndLt: DateTime
    $datetimeEndGt: DateTime
    $overlapsWith: [ID!]
  ) {
    allTimeEntries(
      user: $user
      datetimeStart_Lt: $datetimeStartLt
      datetimeStart_Gt: $datetimeStartGt
      datetimeEnd_Lt: $datetimeEndLt
      datetimeEnd_Gt: $datetimeEndGt
      overlapsWith: $overlapsWith
    ) {
      edges {
        node {
          id

          datetimeStart
          datetimeEnd

          comment
          overtimeMode

          totalOrdinary
          totalWork
          pause
          overtime
          diet

          salaryCodeOrdinary {
            id
            name
            externalId
          }

          salaryCodeOvertime {
            id
            name
            externalId
          }

          facility {
            id
            name
          }

          department {
            id
            name
          }

          latestActionLog {
            id
            action
            actionFields
            createdAt
            user {
              id
              fullName
            }
          }

          user {
            id
            fullName
            userTypes {
              id
            }
          }
        }
      }
    }
  }
`)

export const PENDING_ABSENCE_COUNT_QUERY = graphql(`
  query PendingAbsenceCount {
    pendingAbsenceCount: absenceCountByStatus(status: PENDING)
  }
`)

export const USER_ABSENCES_QUERY = graphql(`
  query UserAbsences(
    $users: [ID]
    $absenceType: ID
    $requestStatus: String
    $leaderGroup: ID
    $datetimeStartLt: DateTime
    $datetimeStartGte: DateTime
    $datetimeStartLte: DateTime
    $datetimeEndGte: DateTime
    $createdAtGte: DateTime
    $createdAtLte: DateTime
  ) {
    allAbsences(
      users: $users
      absenceType: $absenceType
      requestStatus: $requestStatus
      leaderGroup: $leaderGroup
      datetimeStart_Lt: $datetimeStartLt
      datetimeStart_Gte: $datetimeStartGte
      datetimeStart_Lte: $datetimeStartLte
      datetimeEnd_Gte: $datetimeEndGte
      createdAt_Gte: $createdAtGte
      createdAt_Lte: $createdAtLte
      orderBy: "status"
    ) {
      total
      edges {
        node {
          id

          datetimeStart
          datetimeEnd
          reason
          requestStatus
          lockedUpdate
          lockedDelete

          user {
            id
            fullName
          }

          commentCount

          absenceType {
            id
            name
            color

            preset
            durationMode
          }
          files {
            id
            fileName
            fileUrl
            fileSize
          }
        }
      }
    }
  }
`)

export const ABSENCE_REQUESTS_QUERY = graphql(`
  query AbsenceRequests(
    $users: [ID]
    $absenceType: ID
    $leaderGroup: ID
    $datetimeStartGte: DateTime
    $datetimeStartLte: DateTime
    $datetimeEndGte: DateTime
    $createdAtGte: DateTime
    $createdAtLte: DateTime
    $after: String
    $first: Int!
  ) {
    allAbsences(
      users: $users
      absenceType: $absenceType
      requestStatus: "pending"
      leaderGroup: $leaderGroup
      datetimeStart_Gte: $datetimeStartGte
      datetimeStart_Lte: $datetimeStartLte
      datetimeEnd_Gte: $datetimeEndGte
      createdAt_Gte: $createdAtGte
      createdAt_Lte: $createdAtLte
      orderBy: "-datetimeStart"
      after: $after
      first: $first
    ) {
      total
      edges {
        node {
          id

          datetimeStart
          datetimeEnd
          reason
          requestStatus
          files {
            id
            fileName
            fileUrl
          }

          user {
            id
            fullName
          }
          absenceType {
            id
            name
            color

            preset
            durationMode
          }
          periodReport {
            periodStart
            periodEnd

            hoursMax
            hoursUsed
            hoursRemaining
            absencesTotal
            absencesMax
            absencesRemaining

            absenceType {
              name
              durationMode
              maxHoursContinuous
            }
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`)

export const ABSENCE_REQUESTS_GANTT_QUERY = graphql(`
  query AbsenceRequestsGantt(
    $users: [ID]
    $absenceType: ID
    $leaderGroup: ID
    $includeSuperuser: Boolean
    $datetimeStartGte: DateTime
    $datetimeStartLte: DateTime
    $datetimeEndGte: DateTime
    $createdAtGte: DateTime
    $createdAtLte: DateTime
    $first: Int!
  ) {
    pendingAbsences: allAbsences(
      users: $users
      absenceType: $absenceType
      requestStatus: "pending"
      leaderGroup: $leaderGroup
      datetimeStart_Gte: $datetimeStartGte
      datetimeStart_Lte: $datetimeStartLte
      datetimeEnd_Gte: $datetimeEndGte
      createdAt_Gte: $createdAtGte
      createdAt_Lte: $createdAtLte
      orderBy: "-datetimeStart"
      first: $first
    ) {
      edges {
        node {
          id

          datetimeStart
          datetimeEnd
          reason
          requestStatus
          files {
            id
            fileName
            fileUrl
          }

          user {
            id
            fullName
          }
          absenceType {
            id
            name
            color

            preset
            durationMode
          }
          periodReport {
            periodStart
            periodEnd

            hoursMax
            hoursUsed
            hoursRemaining
            absencesTotal
            absencesMax
            absencesRemaining

            absenceType {
              name
              durationMode
              maxHoursContinuous
            }
          }
        }
      }
    }

    approvedAbsences: allAbsences(
      users: $users
      absenceType: $absenceType
      requestStatus: "approved"
      leaderGroup: $leaderGroup
      datetimeStart_Gte: $datetimeStartGte
      datetimeStart_Lte: $datetimeStartLte
      datetimeEnd_Gte: $datetimeEndGte
      createdAt_Gte: $createdAtGte
      createdAt_Lte: $createdAtLte
      orderBy: "-datetimeStart"
      first: $first
    ) {
      edges {
        node {
          id

          datetimeStart
          datetimeEnd
          reason
          requestStatus

          user {
            id
            fullName
          }
          files {
            id
            fileName
            fileUrl
          }
          absenceType {
            id
            name
            color

            preset
            durationMode
          }
          periodReport {
            periodStart
            periodEnd

            hoursMax
            hoursUsed
            hoursRemaining
            absencesTotal
            absencesMax
            absencesRemaining

            absenceType {
              name
              durationMode
              maxHoursContinuous
            }
          }
        }
      }
    }

    allUsersList(
      orderBy: "lastName"
      includeSuperuser: $includeSuperuser
      includeInactive: false
    ) {
      id
      fullName
    }
  }
`)

export const ABSENCE_TYPE_PERIOD_REPORT_QUERY = graphql(`
  query AbsenceTypePeriodReport(
    $userId: ID!
    $absenceTypeId: ID!
    $datetimeStart: DateTime!
    $datetimeEnd: DateTime!
    $excludeAbsenceId: ID
  ) {
    absenceTypePeriodReport(
      userId: $userId
      absenceTypeId: $absenceTypeId
      datetimeStart: $datetimeStart
      datetimeEnd: $datetimeEnd
      excludeAbsenceId: $excludeAbsenceId
    ) {
      absenceType {
        id
        name
        interval
        durationMode
        maxHoursContinuous
        preset
      }

      periodStart
      periodEnd

      hoursMax
      hoursUsed
      hoursRequested
      hoursRemaining

      absencesTotal
      absencesMax
      absencesRemaining
    }
  }
`)

export const ALL_ABSENCES_QUERY = gql`
  query AllAbsences($first: Int!, $orderBy: String) {
    allAbsences(first: $first, orderBy: $orderBy) {
      edges {
        node {
          id
          absenceType {
            id
            name
          }
          datetimeStart
          datetimeEnd
          reason
          user {
            ...UserInfo
          }
        }
      }
    }
  }
  ${UserFragments.info}
`

export const ALL_ABSENCE_TYPES_QUERY = graphql(`
  query AllAbsenceTypes {
    allAbsenceTypes {
      id
      externalId
      defaultDayValue

      name
      color
      preset

      interval
      resetMode
      durationMode

      maxHours
      maxHoursContinuous
      maxAbsencesInInterval

      workDays
      salaryDays
    }
  }
`)

export const ABSENCE_TYPE_MANAGEMENT_QUERY = graphql(`
  query AbsenceTypeManagement($userId: ID!, $date: DateTime!) {
    allAbsenceTypes {
      id
      name
      color

      preset
      durationMode

      periodReport(userId: $userId, date: $date) {
        periodStart
        periodEnd

        hoursMax
        hoursUsed
        hoursRemaining
        absencesMax
        absencesTotal
        absencesRemaining

        user {
          id
          fullName
        }
      }
    }
  }
`)

export const ABSENCE_TYPES_COMBOBOX_QUERY = graphql(`
  query AbsenceTypesCombobox {
    allAbsenceTypes {
      id
      name
      color

      preset
      durationMode
    }
    allUsersList {
      id
      childcare {
        numberOfChildren
      }
    }
  }
`)

export const ACTIVE_ABSENCE_LOCK_QUERY = graphql(`
  query ActiveAbsenceLock {
    activeAbsenceLock {
      id
      name
      lockActions
      adminOverride
      permanent
    }
  }
`)

export const ABSENCE_LOCKED_BEFORE_QUERY = graphql(`
  query AbsenceLockedBefore {
    absenceLockedBefore {
      create
      update
      delete
    }
  }
`)

export const ABSENCE_LOCK_CONFIG_QUERY = graphql(`
  query AbsenceLockConfig($id: ID!) {
    absenceLockConfig(id: $id) {
      id
      name
      lockActions
      adminOverride
      permanent

      rules {
        id
        enabled
        ruleType
        startDate
        durationDays
        rrule
        until
      }
    }
  }
`)

export const ABSENCE_LOCK_CONFIGS_QUERY = graphql(`
  query AbsenceLockConfigs($includeTransient: Boolean = false) {
    absenceLockConfigs(includeTransient: $includeTransient) {
      id
      name
      lockActions
      adminOverride
      permanent

      rules {
        id
      }
    }
  }
`)

export const TIMESHEETS_REPORT_QUERY = graphql(`
  query TimesheetsReport(
    $userId: ID!
    $startDate: DateTime!
    $endDate: DateTime!
    $includeWholeWeeks: Boolean
  ) {
    timesheetsReport(
      userId: $userId
      startDate: $startDate
      endDate: $endDate
      includeWholeWeeks: $includeWholeWeeks
    ) {
      totalOrdinary
      totalWork
      totalPause
      overtime
      user {
        id
        fullName
        userTypes {
          id
          name
        }
      }
      salaryWorkTime {
        id
        pauseThreshold
        pauseDuration
        salaryDateStart
        salaryDateEnd
        availableDepartments {
          id
          name
        }
        defaultDepartment {
          id
          name
        }
        dailyWorkLimit
      }
      periodReports {
        periodStart
        periodEnd

        totalOrdinary
        totalWork
        totalPause

        periodOvertime
        dayOvertime
        overtime

        dayReports {
          totalOrdinary
          totalWork
          totalOvertime
          totalPause

          day
          dayStart
          dayEnd

          comments

          project {
            id
            name
          }
          department {
            id
            name
          }

          timeEntries {
            id
            comment
            datetimeStart
            datetimeEnd

            totalOrdinary
            totalWork
            pause
            overtime
            user {
              id
            }
            diet
            salaryCodeOvertime {
              id
              name
              externalId
            }
            salaryCodeOrdinary {
              id
              name
              externalId
            }
            facility {
              id
              name
            }
            department {
              id
              name
            }
            latestActionLog {
              id
              action
              actionFields
              createdAt
              user {
                id
                fullName
              }
            }
          }
          absences {
            id
            datetimeStart
            datetimeEnd
            reason
            requestStatus
            absenceType {
              id
              name
            }
            user {
              id
              fullName
              userTypes {
                id
              }
            }
          }
          overtimeEntries {
            startTime
            endTime
            overtime
            salaryCode {
              id
              name
              externalId
            }
            project {
              id
              name
            }
            department {
              id
              name
            }
          }
        }
      }
    }
  }
`)

export const ABSENCE_DETAILS_QUERY = graphql(`
  query AbsenceDetails($id: ID!) {
    absence(id: $id) {
      id
      datetimeStart
      datetimeEnd
      reason
      requestStatus
      ownerApprovalStatus
      commentCount
      registeredBy {
        id
        fullName
      }
      user {
        id
        fullName
      }
      absenceType {
        id
        name
        durationMode
      }
      files {
        id
        fileName
        fileUrl
        fileSize
        createdAt
        uploadedBy {
          id
          fullName
        }
      }
      comments {
        id
        content
        createdAt
        updatedAt
        user {
          id
          fullName
          profilePicture
          initials
        }
      }
      actionLogs {
        id
        action
        actionFields
        createdAt
        user {
          id
          fullName
        }
      }
    }
  }
`)

export const TIME_ENTRY_DETAILS_QUERY = graphql(`
  query TimeEntryDetails($id: ID!) {
    timeEntry(id: $id) {
      id
      datetimeStart
      datetimeEnd
      comment
      approved
      diet
      fromTerminal
      overtimeMode
      pause
      totalWork
      overtime
      user {
        id
        fullName
      }
      facility {
        id
        name
      }
      department {
        id
        name
      }
      salaryCodeOrdinary {
        id
        name
      }
      salaryCodeOvertime {
        id
        name
      }
      actionLogs {
        id
        action
        actionFields
        createdAt
        user {
          id
          fullName
        }
      }
    }
  }
`)

export const TIME_ENTRY_ACTION_LOG_QUERY = graphql(`
  query TimeEntryActionLog($id: ID!) {
    timeEntryActionLog(id: $id) {
      id
      action
      actionFields
      createdAt
      timeEntry {
        id
      }
      user {
        id
        fullName
      }
    }
  }
`)
